import React, { useState, useEffect, useRef } from 'react';

/**
 * EditableText Component
 * Renders an HTML input field for inline text editing outside the Konva canvas
 * Positioned absolutely to overlay the text shape being edited
 */
const EditableText = ({ shape, onUpdate, onFinishEdit, stageScale = 1, stagePosition = { x: 0, y: 0 } }) => {
  const [text, setText] = useState(shape.text || '');
  const inputRef = useRef(null);
  
  // Auto-focus and select text when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);
  
  // Calculate screen position accounting for stage scale and position
  const calculatePosition = () => {
    const screenX = shape.x * stageScale + stagePosition.x;
    const screenY = shape.y * stageScale + stagePosition.y;
    
    return {
      left: `${screenX}px`,
      top: `${screenY}px`
    };
  };
  
  const handleSubmit = () => {
    const finalText = text.trim() || 'Text';
    onUpdate(shape.id, { 
      text: finalText,
      isEditing: false 
    });
    onFinishEdit();
  };
  
  const handleKeyDown = (e) => {
    e.stopPropagation(); // Prevent canvas shortcuts from firing
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Cancel editing without saving
      onUpdate(shape.id, { 
        text: shape.text || 'Text', // Revert to original text
        isEditing: false 
      });
      onFinishEdit();
    }
  };
  
  const position = calculatePosition();
  const fontSize = (shape.fontSize || 18) * stageScale;
  
  return (
    <div 
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        zIndex: 10000,
        pointerEvents: 'auto'
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSubmit}
        placeholder="Type your text..."
        style={{
          fontSize: `${fontSize}px`,
          fontFamily: shape.fontFamily || 'Arial, sans-serif',
          color: shape.fill || '#000000',
          border: '2px solid #3b82f6',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '4px 8px',
          outline: 'none',
          minWidth: '120px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        }}
      />
      <div 
        style={{
          position: 'absolute',
          top: '-24px',
          left: '0',
          fontSize: '11px',
          color: '#666',
          whiteSpace: 'nowrap',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '2px 6px',
          borderRadius: '3px',
          border: '1px solid #ddd'
        }}
      >
        <kbd>Enter</kbd> to save • <kbd>Esc</kbd> to cancel
      </div>
    </div>
  );
};

export default EditableText;

